import * as THREE from "three";

const CREAM = new THREE.Color("#F2EDDF");
const STONE_LIGHT = new THREE.Color("#DDD3BF");
const STONE_WARM = new THREE.Color("#D2C2A7");
const AMBER = "#D9A866";
const ICE = "#B7D3DE";
const SAGE = "#8DA186";

const createGlowTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 192;
  const context = canvas.getContext("2d");
  const gradient = context.createRadialGradient(64, 150, 5, 64, 125, 120);
  gradient.addColorStop(0, "rgba(233,206,156,0.95)");
  gradient.addColorStop(0.5, "rgba(217,168,102,0.35)");
  gradient.addColorStop(1, "rgba(217,168,102,0)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 128, 192);
  return new THREE.CanvasTexture(canvas);
};

const createArchGeometry = (width, height, thickness, depth) => {
  const radius = width / 2;
  const shoulder = height - radius;
  const outer = new THREE.Shape();
  outer.moveTo(-width / 2, 0);
  outer.lineTo(-width / 2, shoulder);
  outer.absarc(0, shoulder, radius, Math.PI, 0, true);
  outer.lineTo(width / 2, 0);
  outer.closePath();
  const inner = new THREE.Path();
  inner.moveTo(-width / 2 + thickness, 0);
  inner.lineTo(-width / 2 + thickness, shoulder);
  inner.absarc(0, shoulder, radius - thickness, Math.PI, 0, true);
  inner.lineTo(width / 2 - thickness, 0);
  inner.closePath();
  outer.holes.push(inner);
  return new THREE.ExtrudeGeometry(outer, { depth, bevelEnabled: false, curveSegments: 8, steps: 1 });
};

const createArchFillGeometry = (width, height) => {
  const radius = width / 2;
  const shoulder = height - radius;
  const shape = new THREE.Shape();
  shape.moveTo(-width / 2, 0);
  shape.lineTo(-width / 2, shoulder);
  shape.absarc(0, shoulder, radius, Math.PI, 0, true);
  shape.lineTo(width / 2, 0);
  shape.closePath();
  return new THREE.ShapeGeometry(shape, 10);
};

const addArch = ({ scene, stoneMaterials, edgeMaterials, width, height, thickness, x, z, rotationY, edgeColor, edgeOpacity }) => {
  const geometry = createArchGeometry(width, height, thickness, 2.4);
  const mesh = new THREE.Mesh(geometry, stoneMaterials);
  mesh.position.set(x, 0, z);
  mesh.rotation.y = rotationY;
  const lineMaterial = new THREE.LineBasicMaterial({ color: edgeColor, transparent: true, opacity: edgeOpacity });
  lineMaterial.userData.baseOpacity = edgeOpacity;
  const line = new THREE.LineSegments(new THREE.EdgesGeometry(geometry, 28), lineMaterial);
  line.position.copy(mesh.position);
  line.rotation.copy(mesh.rotation);
  scene.add(mesh, line);
  edgeMaterials.push(lineMaterial);
};

const addArchitecture = (scene) => {
  const stoneMaterials = [
    new THREE.MeshBasicMaterial({ color: STONE_LIGHT, fog: true }),
    new THREE.MeshBasicMaterial({ color: "#C2B69F", fog: true }),
  ];
  const edgeMaterials = [];
  const arches = [
    [17, 24, 1.3, 0, -26, 0, AMBER, 0.3],
    [12, 17, 1, -16, -13, 0.28, ICE, 0.24],
    [13, 19, 1.1, 15, -18, -0.24, AMBER, 0.22],
    [8.5, 12, 0.8, 24, -34, -0.4, ICE, 0.2],
  ];
  arches.forEach(([width, height, thickness, x, z, rotationY, edgeColor, edgeOpacity]) => {
    addArch({ scene, stoneMaterials, edgeMaterials, width, height, thickness, x, z, rotationY, edgeColor, edgeOpacity });
  });

  const apertureMaterial = new THREE.MeshBasicMaterial({ color: "#FBF5E8", transparent: true, opacity: 0.72, depthWrite: false });
  const aperture = new THREE.Mesh(createArchFillGeometry(14.4, 21.4), apertureMaterial);
  aperture.position.set(0, 0, -25.8);
  scene.add(aperture);
  return { stoneMaterials, edgeMaterials, apertureMaterial, aperture };
};

const createVeil = (width, height, color, edgeColor) => {
  const group = new THREE.Group();
  const geometry = new THREE.PlaneGeometry(width, height);
  const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.16, side: THREE.DoubleSide, depthWrite: false });
  const edgeMaterial = new THREE.LineBasicMaterial({ color: edgeColor, transparent: true, opacity: 0.2 });
  group.add(new THREE.Mesh(geometry, material));
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgeMaterial));
  return group;
};

const addFloorAndVeils = (scene) => {
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(160, 160),
    new THREE.MeshBasicMaterial({ color: "#E9E1CE", fog: true })
  );
  floor.rotation.x = -Math.PI / 2;
  const grid = new THREE.GridHelper(160, 32, "#B2AA98", "#C8C0AE");
  grid.material.transparent = true;
  grid.material.opacity = 0.045;
  grid.position.y = 0.02;
  const veilA = createVeil(15, 21, "#B7C8CE", "#8DA1A8");
  veilA.position.set(-8, 10, -9);
  veilA.rotation.y = 0.42;
  veilA.userData.baseY = 10;
  const veilB = createVeil(12, 17, "#E0C4B8", "#C5A99E");
  veilB.position.set(10, 9, -20);
  veilB.rotation.y = -0.35;
  veilB.userData.baseY = 9;
  const veilC = createVeil(7, 11, "#E9CE9C", "#C9A86E");
  veilC.position.set(5.5, 6.2, -5);
  veilC.rotation.y = -0.18;
  veilC.userData.baseY = 6.2;
  scene.add(floor, grid, veilA, veilB, veilC);
  return { grid, veils: [veilA, veilB, veilC] };
};

const addThreadsAndGlow = (scene) => {
  const threadMaterial = new THREE.MeshBasicMaterial({ color: SAGE, transparent: true, opacity: 0.48 });
  const threadCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(-26, 8, -6), new THREE.Vector3(-8, 14, -18),
    new THREE.Vector3(10, 7, -24), new THREE.Vector3(28, 12, -10),
  ]);
  scene.add(new THREE.Mesh(new THREE.TubeGeometry(threadCurve, 40, 0.045, 4, false), threadMaterial));

  const guideMaterial = new THREE.MeshBasicMaterial({ color: AMBER, transparent: true, opacity: 0.44 });
  const guideCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(0, 1.1, 8), new THREE.Vector3(1.6, 2.6, -6),
    new THREE.Vector3(-1.1, 4.2, -16), new THREE.Vector3(0, 5.2, -24.3),
  ]);
  scene.add(new THREE.Mesh(new THREE.TubeGeometry(guideCurve, 40, 0.052, 4, false), guideMaterial));
  const depthThreadMaterial = new THREE.MeshBasicMaterial({ color: ICE, transparent: true, opacity: 0.28 });
  const depthThreadCurve = new THREE.CatmullRomCurve3([
    new THREE.Vector3(-22, 5, -30), new THREE.Vector3(-7, 11, -25),
    new THREE.Vector3(8, 13, -19), new THREE.Vector3(23, 8, -27),
  ]);
  scene.add(new THREE.Mesh(new THREE.TubeGeometry(depthThreadCurve, 32, 0.035, 4, false), depthThreadMaterial));
  const sparkMaterial = new THREE.MeshBasicMaterial({ color: "#F3D9A8", transparent: true, opacity: 0.3 });
  const spark = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8), sparkMaterial);
  scene.add(spark);

  const glowTexture = createGlowTexture();
  const glowMaterial = new THREE.MeshBasicMaterial({ map: glowTexture, transparent: true, opacity: 0.18, depthWrite: false });
  const glow = new THREE.Mesh(new THREE.PlaneGeometry(14, 21), glowMaterial);
  glow.position.set(0, 8.5, -25.2);
  scene.add(glow);
  return { threadMaterial, guideMaterial, guideCurve, depthThreadMaterial, sparkMaterial, spark, glowTexture, glowMaterial, glow };
};

const createRenderer = (mount) => {
  const canvas = document.createElement("canvas");
  let context = null;
  try {
    context = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: true,
      stencil: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });
  } catch (error) {
    return null;
  }
  if (!context) return null;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    context,
    antialias: false,
    alpha: false,
    powerPreference: "high-performance",
    precision: "mediump",
    stencil: false,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 0.52));
  renderer.setSize(mount.clientWidth, mount.clientHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.domElement.setAttribute("data-testid", "sanctum-webgl-canvas");
  mount.appendChild(renderer.domElement);
  return renderer;
};

const usesSoftwareRenderer = (renderer) => {
  const context = renderer.getContext();
  const debugInfo = context.getExtension("WEBGL_debug_renderer_info");
  if (!debugInfo) return false;
  const rendererName = context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "";
  return /swiftshader|llvmpipe|softpipe|software rasterizer|microsoft basic render|basic render driver|\bwarp\b/i.test(rendererName);
};

const createFallbackRuntime = (renderer, mount) => {
  mount.dataset.renderQuality = "css-fallback";
  renderer.dispose();
  renderer.forceContextLoss();
  if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
  return {
    usesFallback: true,
    setVisible() {},
    dispose() {},
  };
};

const createUnavailableRuntime = (mount) => {
  mount.dataset.renderQuality = "css-fallback";
  return {
    usesFallback: true,
    setVisible() {},
    dispose() {},
  };
};

const createSceneResources = (mount, renderer) => {
  const scene = new THREE.Scene();
  scene.background = CREAM.clone();
  scene.fog = new THREE.Fog(CREAM.clone(), 48, 120);
  const camera = new THREE.PerspectiveCamera(42, mount.clientWidth / mount.clientHeight, 0.1, 220);
  camera.position.set(0, 6.5, 26);
  camera.lookAt(0, 7.4, -12);
  const architecture = addArchitecture(scene);
  const environment = addFloorAndVeils(scene);
  const effects = addThreadsAndGlow(scene);
  return { mount, renderer, scene, camera, ...environment, ...architecture, ...effects };
};

const updateScene = (resources, now, startedAt, progress, mouse) => {
  const time = Math.max(0, (now - startedAt) / 1000);
  const value = progress || 0;
  const depth = THREE.MathUtils.smoothstep(value, 0.18, 0.86);
  const pulse = (Math.sin(time * 0.72) + 1) * 0.5;
  resources.edgeMaterials.forEach((material) => {
    material.opacity = material.userData.baseOpacity + depth * 0.04 + pulse * 0.025;
  });
  resources.grid.material.opacity = 0.045 + depth * 0.025;
  resources.stoneMaterials[0].color.copy(STONE_LIGHT).lerp(STONE_WARM, depth * 0.42);
  resources.stoneMaterials[1].color.set("#C2B69F").lerp(STONE_WARM, depth * 0.2);
  resources.apertureMaterial.opacity = 0.68 + depth * 0.12 + pulse * 0.06;
  resources.aperture.scale.setScalar(1 + pulse * 0.0025);
  resources.glowMaterial.opacity = 0.16 + depth * 0.12 + pulse * 0.05;
  resources.glow.scale.setScalar(0.96 + depth * 0.1 + pulse * 0.018);
  resources.guideMaterial.opacity = 0.4 + depth * 0.16;
  resources.threadMaterial.opacity = 0.42 + depth * 0.1;
  resources.depthThreadMaterial.opacity = 0.22 + depth * 0.12;
  resources.veils.forEach((veil, index) => {
    veil.position.y = veil.userData.baseY + Math.sin(time * 0.22 + index * 1.7) * 0.08 + depth * (index - 1) * 0.35;
  });
  const sparkProgress = THREE.MathUtils.euclideanModulo(time * 0.028 + value * 0.74, 1);
  resources.spark.position.copy(resources.guideCurve.getPointAt(sparkProgress));
  resources.sparkMaterial.opacity = 0.58 + pulse * 0.22;
  resources.spark.scale.setScalar(1 + pulse * 0.45);
  resources.camera.position.z = 26 - value * 10;
  resources.camera.position.y = 6.5 + value * 1.5;
  resources.camera.position.x += (Math.sin(value * Math.PI) * 0.55 - resources.camera.position.x) * 0.06;
  resources.camera.rotation.y += (mouse.x * -0.025 - resources.camera.rotation.y) * 0.08;
  resources.camera.rotation.x += (-0.01 + mouse.y * -0.02 - resources.camera.rotation.x) * 0.08;
};

const disposeResources = (resources) => {
  resources.scene.traverse((object) => {
    object.geometry?.dispose();
    if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
    else object.material?.dispose();
  });
  resources.glowTexture.dispose();
  resources.renderer.dispose();
  resources.renderer.forceContextLoss();
  if (resources.renderer.domElement.parentNode === resources.mount) {
    resources.mount.removeChild(resources.renderer.domElement);
  }
};

export const createSanctumRuntime = ({ mount, progressRef }) => {
  const renderer = createRenderer(mount);
  if (!renderer) return createUnavailableRuntime(mount);
  if (usesSoftwareRenderer(renderer)) return createFallbackRuntime(renderer, mount);
  mount.dataset.renderQuality = "balanced";
  const resources = createSceneResources(mount, renderer);
  const mouse = { x: 0, y: 0 };
  const startedAt = performance.now();
  let animationFrame = 0;
  let lastFrame = 0;
  let visible = false;
  let disposed = false;
  let renderCostTotal = 0;
  let renderSamples = 0;
  let qualityAdjusted = false;

  const reduceRenderCost = () => {
    qualityAdjusted = true;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 0.4));
    renderer.setSize(mount.clientWidth, mount.clientHeight, false);
    resources.veils[0].visible = false;
    resources.veils[1].visible = false;
    resources.glow.visible = false;
    renderer.domElement.dataset.renderQuality = "adaptive";
  };

  const renderFrame = (now) => {
    if (!visible || disposed) {
      animationFrame = 0;
      return;
    }
    animationFrame = requestAnimationFrame(renderFrame);
    if (now - lastFrame < 40) return;
    lastFrame = now;
    updateScene(resources, now, startedAt, progressRef.current, mouse);
    const renderStartedAt = performance.now();
    renderer.render(resources.scene, resources.camera);
    if (!qualityAdjusted && renderSamples < 12) {
      renderCostTotal += performance.now() - renderStartedAt;
      renderSamples += 1;
      if (renderSamples === 12 && renderCostTotal / renderSamples > 24) reduceRenderCost();
    }
  };
  const onPointerMove = (event) => {
    mouse.x = event.clientX / window.innerWidth - 0.5;
    mouse.y = event.clientY / window.innerHeight - 0.5;
  };
  const onResize = () => {
    const width = mount.clientWidth;
    const height = mount.clientHeight;
    resources.camera.aspect = width / height;
    resources.camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  };
  window.addEventListener("pointermove", onPointerMove, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });

  return {
    usesFallback: false,
    setVisible(nextVisible) {
      visible = nextVisible;
      if (visible && !animationFrame) {
        lastFrame = performance.now() - 33;
        animationFrame = requestAnimationFrame(renderFrame);
      } else if (!visible) {
        cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      visible = false;
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", onResize);
      disposeResources(resources);
    },
  };
};